import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: localStorage,
    storageKey: 'punchout-auth',
    flowType: 'pkce',
  },
})

// Force a session refresh and return a fresh access_token. Use this
// before admin API calls (approve-user, delete-user) — the server-side
// supabase.auth.getUser(token) check rejects expired tokens with
// "Invalid token", and the client's autoRefreshToken timer is
// unreliable after long iOS PWA backgrounding (suspended timers don't
// fire in time). Always-refresh costs one extra request per admin
// action; admin actions are rare and not perf-sensitive.
export async function getFreshAdminToken(): Promise<string> {
  const { data, error } = await supabase.auth.refreshSession()
  if (error || !data.session) {
    throw new Error('Session expired — sign in again')
  }
  return data.session.access_token
}

// Storage bucket names
export const STORAGE_BUCKET = 'photos'

// The photos bucket is private. Read URLs are minted as cached signed URLs —
// see src/lib/signedUrls.ts (signedPhotoUrl / useSignedUrl / getSignedUrlAsync).
// The old getPublicUrl / getResizedPublicUrl helpers were removed in the
// private-bucket migration.

// Helper to upload file with progress
export async function uploadFile(
  file: File | Blob,
  path: string
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    })

  if (error) throw error
  return data.path
}
